import {
  StaffSearchParams,
  StaffListResponse,
  StaffActionResponse,
  StaffApproveFormData,
  StaffLeaveFormData,
  StaffCreateFormData,
  CustomerSearchParams,
  CustomerSearchResponse,
  ImageUploadValues,
  StaffStatsFormData,
  StaffStatsUpdateResponse,
  StaffProfileFormData,
  StaffProfileUpdateResponse,
  AvailableVisitStaffMember,
} from "../types/staff.types";
import { apiRequest } from "./apiUtils";
import { API_CONFIG } from "../config/api.config";

/**
 * 员工管理 API 服务
 * 使用公共的 apiRequest 方法进行统一的API调用
 */
export class StaffService {
  /**
   * 获取员工列表
   */
  static async getServiceStaffList(
    params: StaffSearchParams
  ): Promise<StaffListResponse> {
    try {
      const response = await apiRequest<StaffListResponse>(
        API_CONFIG.ENDPOINTS.STAFF_SERVICE,
        "GET",
        params,
        true
      );
      return response;
    } catch (error) {
      console.error("获取员工列表失败:", error);
      throw new Error("获取员工列表失败，请重试");
    }
  }

  /**
   * 根据ID获取员工详情
   */
  static async getStaffDetail(id: number): Promise<StaffActionResponse> {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.STAFF_DETAIL(id)}`;
      const response = await apiRequest<StaffActionResponse>(
        endpoint,
        "GET",
        null,
        true // 需要认证
      );
      return response;
    } catch (error) {
      console.error("获取员工详情失败:", error);
      throw new Error("获取员工详情失败，请重试");
    }
  }

  /**
   * 审核员工入职
   */
  static async approveStaff(
    id: number,
    formData: StaffApproveFormData,
    imageUrls: ImageUploadValues
  ): Promise<StaffActionResponse> {
    try {
      // 构建请求数据 - 现在直接使用JSON而不是FormData
      const requestData = {
        ...formData,
        // 添加图片URL字段
        avatarUrl: imageUrls.avatar,
        idCardFrontUrl: imageUrls.idCardFront,
        idCardBackUrl: imageUrls.idCardBack,
        healthCertUrl: imageUrls.healthCert,
      };

      const endpoint = API_CONFIG.ENDPOINTS.STAFF_APPROVE(id);
      const response = await apiRequest<StaffActionResponse>(
        endpoint,
        "POST",
        requestData,
        true
      );
      return response;
    } catch (error) {
      console.error("审核员工入职失败:", error);
      throw new Error("审核员工入职失败，请重试");
    }
  }

  /**
   * 办理员工离职
   */
  static async leaveStaff(
    id: number,
    formData: StaffLeaveFormData
  ): Promise<StaffActionResponse> {
    try {
      const endpoint = API_CONFIG.ENDPOINTS.STAFF_LEAVE(id);
      const response = await apiRequest<StaffActionResponse>(
        endpoint,
        "PUT",
        formData,
        true // 需要认证
      );
      return response;
    } catch (error) {
      console.error("办理员工离职失败:", error);
      throw new Error("办理员工离职失败，请重试");
    }
  }

  /**
   * 获取员工统计信息
   */
  static async getStaffStats(): Promise<{
    total: number;
    active: number;
    resigned: number;
    pending: number;
  }> {
    try {
      const response = await apiRequest<{
        data: {
          total: number;
          active: number;
          resigned: number;
          pending: number;
        };
      }>(
        API_CONFIG.ENDPOINTS.STAFF_STATS,
        "GET",
        null,
        true // 需要认证
      );
      return response.data;
    } catch (error) {
      console.error("获取员工统计信息失败:", error);
      throw new Error("获取员工统计信息失败，请重试");
    }
  }

  /**
   * 搜索可成为员工的客户
   */
  static async searchCustomersForStaff(
    params: CustomerSearchParams
  ): Promise<CustomerSearchResponse> {
    try {
      const response = await apiRequest<CustomerSearchResponse>(
        API_CONFIG.ENDPOINTS.STAFF_CUSTOMERS_SEARCH,
        "GET",
        params,
        true
      );
      return response;
    } catch (error) {
      console.error("搜索客户失败:", error);
      throw new Error("搜索客户失败，请重试");
    }
  }

  /**
   * 新增员工
   */
  static async createStaff(
    formData: StaffCreateFormData,
    imageUrls: ImageUploadValues
  ): Promise<StaffActionResponse> {
    try {
      // 构建请求数据 - 现在直接使用JSON而不是FormData
      const requestData = {
        ...formData,
        // 添加图片URL字段
        avatarUrl: imageUrls.avatar,
        idCardFrontUrl: imageUrls.idCardFront,
        idCardBackUrl: imageUrls.idCardBack,
        healthCertUrl: imageUrls.healthCert,
      };

      const response = await apiRequest<StaffActionResponse>(
        API_CONFIG.ENDPOINTS.STAFF_CREATE,
        "POST",
        requestData,
        true
      );
      return response;
    } catch (error) {
      console.error("新增员工失败:", error);
      throw new Error("新增员工失败，请重试");
    }
  }

  /**
   * 修改员工统计数据
   */
  static async updateStaffIndexes(
    id: number,
    statsData: StaffStatsFormData
  ): Promise<StaffStatsUpdateResponse> {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.STAFF_INDEXES(id)}`;
      const response = await apiRequest<StaffStatsUpdateResponse>(
        endpoint,
        "PATCH",
        statsData,
        true
      );
      return response;
    } catch (error) {
      console.error("修改员工统计失败:", error);
      throw new Error("修改员工统计失败，请重试");
    }
  }

  /**
   * 修改员工资料
   */
  static async updateStaffProfile(
    id: number,
    profileData: StaffProfileFormData,
    files?: ImageUploadValues
  ): Promise<StaffProfileUpdateResponse> {
    try {
      // 上传图片文件（如果有的话）
      const imageUrls: { [key: string]: string | undefined } = {};

      if (files) {
        if (files.avatar) {
          imageUrls.avatar = files.avatar;
        }
        if (files.idCardFront) {
          imageUrls.idCardFront = files.idCardFront;
        }
        if (files.idCardBack) {
          imageUrls.idCardBack = files.idCardBack;
        }
        if (files.healthCert) {
          imageUrls.healthCert = files.healthCert;
        }
      }

      // 构建请求数据
      const requestData = {
        ...profileData,
        avatarUrl: imageUrls.avatar,
        idCardFrontUrl: imageUrls.idCardFront,
        idCardBackUrl: imageUrls.idCardBack,
        healthCertUrl: imageUrls.healthCert,
      };

      const endpoint = `${API_CONFIG.ENDPOINTS.STAFF_PROFILE(id)}`;
      const response = await apiRequest<StaffProfileUpdateResponse>(
        endpoint,
        "PATCH",
        requestData
      );
      return response;
    } catch (error) {
      console.error("修改员工资料失败:", error);
      throw new Error("修改员工资料失败，请重试");
    }
  }

  /**
   * 获取可用的随访服务人员列表
   * @param elderId 老人ID，用于计算员工工作负载
   */
  static async getAvailableVisitServiceStaff(
    elderId: number
  ): Promise<AvailableVisitStaffMember[]> {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: any[];
        timestamp: string;
      }>(`${API_CONFIG.ENDPOINTS.STAFF_AVAILABLE(elderId)}`, "GET", null);

      // 将服务端数据格式转换为前端期望的格式
      const transformedData: AvailableVisitStaffMember[] = response.data.map(
        (staff: any) => ({
          id: staff.id.toString(),
          avatar: staff.avatar || "",
          name: staff.name || "",
          gender: staff.gender === 1 ? "男" : "女",
          age: staff.age || 0,
          phone: staff.phone || "",
          address: staff.address || "",
          idCard: staff.idCard || "",
          joinDate: staff.onBoard
            ? new Date(staff.onBoard).toISOString().split("T")[0]
            : "",
          serviceCount: staff.serviceCount || 0,
          customerCount: staff.customerCount || 0,
          availableVisitCount: staff.availableVisitCount || 0,
          rating: (staff.rating || 0).toFixed(1),
        })
      );

      return transformedData;
    } catch (error) {
      console.error("获取可用服务人员失败:", error);
      throw new Error("获取可用服务人员失败，请重试");
    }
  }
}

export default StaffService;
